import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  HoverableLink,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import Processing from 'ext-ui/components/Processing/Processing'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  selectErrorDetailMapFromState,
  selectIsPostingUserFromState,
  selectTopLevelErrorFromState,
} from 'src/app/Signup/redux/signup.selectors'
import SignupSsoForm from 'src/component-form/SignupSsoForm/SignupSsoForm'
import AppName from 'src/components/AppName/AppName'
import BrandPrimaryLogo from 'src/components/BrandPrimaryLogo/BrandPrimaryLogo'
import ResourceCenterLink from 'src/components/ResourceCenterLink/ResourceCenterLink'
import SignupForm from 'src/components/SignupForm/SignupForm'
import SvgCheckFilledPurple from 'src/components/Svgs/SvgCheckFilledPurple'
import UnauthedBoxContainer from 'src/components/UnauthedBoxContainer/UnauthedBoxContainer'
import UnauthedFooter from 'src/components/UnauthedFooter/UnauthedFooter'
import UnauthedPageContainer from 'src/components/UnauthedPageContainer/UnauthedPageContainer'
import { SupportedBrands } from 'src/enums'
import { useBackgroundDispatch, useWindowSize } from 'src/hooks'
import Trns, { useTranslate } from 'src/localization/Trns'
import { createSetIsExtraVerificationPopupOpen } from 'src/redux/actions/app.actions'
import {
  createSetEmail,
  POST_USER_SUCCESS,
  postUserAndRedirectToSignin,
} from 'src/redux/actions/auth.actions'
import { selectEmailFromState } from 'src/redux/selectors/auth.selectors'
import { selectQueryParams } from 'src/redux/selectors/router.selectors'
import { selectBrandName } from 'src/redux/selectors/session.selectors'
import { PostUserRequest } from 'src/requests/postUser.request'
import styled, { css } from 'styled-components'

export type SignupQueryParams = {
  orgName?: string
  inviteeEmail?: string
  inviterName?: string
  inviterEmail: string
}

const SignupBulletDiv = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  &:nth-last-child(n + 2) {
    margin-bottom: 20px;
  }
`

const SignupBulletCopyDiv = styled.div`
  flex-shrink: 2;
`

interface SignupFullPageProps {
  setShowHowToJoin: (val: boolean) => void
  authConfigLoading?: boolean
  isSsoOrg?: boolean
}

const SignupFullPage: React.FC<SignupFullPageProps> = (props) => {
  const email = useSelector(selectEmailFromState)
  const isPostingUser = useSelector(selectIsPostingUserFromState)
  const topLevelError = useSelector(selectTopLevelErrorFromState)
  const errorDetailMap = useSelector(selectErrorDetailMapFromState)
  const brandName = useSelector(selectBrandName)
  const queryParams = useSelector(selectQueryParams)
  const translate = useTranslate()

  const [lessThan680, setLessThan680] = useState<boolean>(false)

  const dispatch = useBackgroundDispatch()

  const dispatchSetEmail = (val: string): void => {
    dispatch(createSetEmail(val))
  }

  const dispatchPostUser = (body: Partial<PostUserRequest>): void => {
    dispatch(postUserAndRedirectToSignin(body, signupAndJoin)).then(
      (res): void => {
        if (res.type === POST_USER_SUCCESS) {
          dispatch(createSetIsExtraVerificationPopupOpen(true))
        }
      },
    )
  }

  const windowHeight = useWindowSize()

  useEffect(() => {
    if (windowHeight[1] < 680) {
      setLessThan680(true)
    } else {
      setLessThan680(false)
    }
  }, windowHeight)

  const { orgName, inviteeEmail, inviterName, inviterEmail } = queryParams

  const signupAndJoin = !!(
    orgName &&
    inviteeEmail &&
    inviterName &&
    inviterEmail
  )

  return (
    <UnauthedPageContainer>
      <div
        css={css`
          display: flex;
          align-items: stretch;
          justify-content: center;
          width: 100%;
          gap: 60px;

          ${getBreakpointDown(breakpointSizes.MD)(css`
            padding: 0px;
          `)}
        `}
      >
        {!signupAndJoin && (
          <div
            css={css`
              width: 50%;

              ${getBreakpointDown(breakpointSizes.MD)(css`
                display: none;
              `)}
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                min-width: 600px;
                max-width: 800px;
                padding-top: 140px;
                right: ${lessThan680 ? 'none' : '50%'};
                position: ${lessThan680 ? 'relative' : 'fixed'};
                padding-left: ${lessThan680 ? '80px' : '160px'};
                float: right;

                ${getBreakpointDown(breakpointSizes.MD)(css`
                  display: none;
                `)}
              `}
            >
              {brandName !== SupportedBrands.Jpm && (
                <BrandPrimaryLogo
                  css={css`
                    margin-bottom: 40px;
                    max-height: 100px;
                    min-height: 25px;
                    max-width: 85px;
                  `}
                />
              )}
              {brandName !== SupportedBrands.PacWest &&
                brandName !== SupportedBrands.Boc && (
                  <div
                    css={css`
                      ${getFontCss(
                        16,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.medium,
                      )};
                    `}
                  >
                    <Trns
                      source={
                        "Achieve more with what's yours. Pay with ${appName}."
                      }
                      expressions={{
                        appName: <AppName />,
                      }}
                    />
                  </div>
                )}
              <div
                css={css`
                  margin-bottom: 40px;
                `}
              >
                <Trns
                  source={
                    "Whether you're the head of an organization, oversee a department, or are a sole proprietor, ${appName} is flexible to your needs."
                  }
                  expressions={{
                    appName: <AppName />,
                  }}
                />
              </div>
              <div
                css={css`
                  margin-bottom: ${brandName === SupportedBrands.Pnc
                    ? '30px'
                    : '60px'};
                `}
              >
                <SignupBulletDiv>
                  <SvgCheckFilledPurple />
                  <SignupBulletCopyDiv>
                    <div
                      css={css`
                        ${getFontCss(
                          16,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )};
                        margin-bottom: 5px;
                      `}
                    >
                      <Trns source={'Get started in minutes'} />
                    </div>
                    <div
                      css={css`
                        ${getFontCss(
                          16,
                          styleConstants.colors.gray_darkest,
                          styleConstants.fontFamilies.regular,
                        )};
                      `}
                    >
                      <Trns
                        source={
                          'Register your company card and create virtual cards right away'
                        }
                      />
                    </div>
                  </SignupBulletCopyDiv>
                </SignupBulletDiv>
                <SignupBulletDiv>
                  <SvgCheckFilledPurple />
                  <SignupBulletCopyDiv>
                    <div
                      css={css`
                        ${getFontCss(
                          16,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )};
                        margin-bottom: 5px;
                      `}
                    >
                      <Trns source={'Invite your team'} />
                    </div>
                    <div
                      css={css`
                        ${getFontCss(
                          16,
                          styleConstants.colors.gray_darkest,
                          styleConstants.fontFamilies.regular,
                        )};
                      `}
                    >
                      <Trns
                        source={
                          'Bring the whole team to ${appName} and manage employee expenses all in one place'
                        }
                        expressions={{
                          appName: <AppName />,
                        }}
                      />
                    </div>
                  </SignupBulletCopyDiv>
                </SignupBulletDiv>
                <SignupBulletDiv>
                  <SvgCheckFilledPurple />
                  <SignupBulletCopyDiv>
                    <div
                      css={css`
                        ${getFontCss(
                          16,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )};
                        margin-bottom: 5px;
                      `}
                    >
                      <Trns source={'Organize spend'} />
                    </div>
                    <div
                      css={css`
                        ${getFontCss(
                          16,
                          styleConstants.colors.gray_darkest,
                          styleConstants.fontFamilies.regular,
                        )};
                      `}
                    >
                      <Trns
                        source={
                          'Allocate budgets, set up expense categories, and integrate with accounting software'
                        }
                      />
                    </div>
                  </SignupBulletCopyDiv>
                </SignupBulletDiv>
              </div>
              <div>
                <div
                  css={css`
                    ${getFontCss(
                      12,
                      styleConstants.colors.gray_darkest,
                      styleConstants.fontFamilies.regular,
                    )};
                  `}
                >
                  <>
                    {brandName === SupportedBrands.Extend && (
                      <div
                        css={css`
                          margin-bottom: 10px;
                        `}
                      >
                        <Trns
                          source={'Want a demo? ${link}.'}
                          expressions={{
                            link: (
                              <HoverableLink
                                size={HoverableLinkSizes.Small}
                                href={'https://paywithextend.com/get-a-demo'}
                                underline
                                regular
                                ariaLabelSource={
                                  'Schedule a 1:1 demo with our experts'
                                }
                              >
                                <Trns
                                  source={
                                    'Schedule a 1:1 demo with our experts'
                                  }
                                />
                              </HoverableLink>
                            ),
                          }}
                        />
                      </div>
                    )}
                    <Trns
                      source={
                        'Need help? Visit our ${resourceCenter} for support and contact options.'
                      }
                      expressions={{
                        resourceCenter: (
                          <ResourceCenterLink
                            size={HoverableLinkSizes.Small}
                            underline
                            regular
                          />
                        ),
                      }}
                    />
                    {brandName === SupportedBrands.Pnc && (
                      <div
                        css={css`
                          margin-top: 30px;
                          line-height: 1.4;
                        `}
                      >
                        {translate(
                          'PNC and PNC Bank are registered marks of The PNC Financial Services Group, Inc. (“PNC”). Bank deposit, treasury management and lending products and services, foreign exchange and derivative products, bond accounting and safekeeping services, escrow services, and investment and wealth management and fiduciary services, are provided by PNC Bank, National Association (“PNC Bank”), a wholly owned subsidiary of PNC and Member FDIC. Lending, leasing and equity products and services, as well as certain other banking products and services, require credit approval.',
                        )}
                        <br />
                        <br />
                        <div className="">
                          {translate(
                            '©2025 The PNC Financial Services Group, Inc. All rights reserved.',
                          )}
                        </div>
                        <br />
                      </div>
                    )}
                  </>
                </div>
              </div>
              <UnauthedFooter
                css={css`
                  display: flex;
                  justify-content: flex-start;
                `}
                isSignUp={
                  brandName !== SupportedBrands.PacWest &&
                  brandName !== SupportedBrands.Boc
                }
                topMargin={false}
              />
            </div>
          </div>
        )}
        <UnauthedBoxContainer
          css={css`
            display: flex;
            flex-direction: column;

            ${!signupAndJoin &&
            css`
              align-items: flex-start;
              width: 50%;
              padding-right: 20px;
            `}

            ${getBreakpointDown(breakpointSizes.MD)(css`
              width: 100%;
              align-items: center;
              padding: 40px 10px;
            `)}
          `}
          hideBrandLogo
          hideFooter={!signupAndJoin}
        >
          {props.authConfigLoading ? (
            <Processing width={48} height={48} />
          ) : props.isSsoOrg &&
            inviteeEmail &&
            orgName &&
            inviterName &&
            inviterEmail ? (
            <SignupSsoForm
              css={css`
                min-width: 400px;
                ${getBreakpointDown(breakpointSizes.XSM)(css`
                  min-width: auto;
                `)}
              `}
              inviteeEmail={inviteeEmail}
              orgName={orgName}
              inviterName={inviterName}
              inviterEmail={inviterEmail}
            />
          ) : (
            <SignupForm
              css={css`
                min-width: 400px;
                ${getBreakpointDown(breakpointSizes.XSM)(css`
                  min-width: auto;
                `)}
              `}
              email={email}
              isPostingUser={isPostingUser}
              topLevelError={topLevelError}
              errorDetailMap={errorDetailMap}
              setEmail={dispatchSetEmail}
              postUser={dispatchPostUser}
              setShowHowToJoin={props.setShowHowToJoin}
              queryParams={queryParams as SignupQueryParams}
            />
          )}
        </UnauthedBoxContainer>
      </div>
    </UnauthedPageContainer>
  )
}

export default SignupFullPage
